/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getViewInheritableGroupState, setViewInheritableGroupState} from '../utils/helpers';

export interface useInheritableGroupParams {
  name: string;
  action: Function;
  preference?: string;
};

/**
 * useInheritableGroup hook used to update the value for Inheritable Group used by all the grids.
 *
 * ```typescript
 *
 * import {useInheritableGroup} from '@ion/applications/src/hooks/';
 *
 * const [viewSubGroups, toggleViewSubGroups] = useInheritableGroup();
 * ```
 */
export const useInheritableGroup = ({name, action, preference = ''}: useInheritableGroupParams) => {
  const dispatch          = useDispatch();
  const inheritableGroups = useSelector(({[name]: {grid}}) => grid?.inheritableGroups) ||
  getViewInheritableGroupState(preference);
  const [
    viewInheritableGroup,
    toggleViewInheritableGroup
  ] = useState(inheritableGroups);

  useEffect(() => {
    dispatch(action(viewInheritableGroup));
    if (preference && inheritableGroups !== viewInheritableGroup) {
      setViewInheritableGroupState(preference, viewInheritableGroup);
    }
  }, [viewInheritableGroup]);

  return [
    viewInheritableGroup,
    toggleViewInheritableGroup
  ];
};

export default useInheritableGroup;
